import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Settings, LogOut, User, ChevronRight } from "lucide-react";
import { useOnboardingGate } from "@/components/routing/OnboardingGate";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { NotificationCard } from "@/components/notifications/NotificationCard";
import { useNotifications } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import { SheetCloseIcon, SHEET_CLOSE_CLASS, SHEET_ACCOUNT_MIN_H_CLASS } from "@/components/ui/sheetChrome";
import { DatosEnEsteMovil } from "@/components/offline/DatosEnEsteMovil";
import { useFotoOffline } from "@/lib/offline/fotosOffline";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Destino del engranaje. El perito no tiene Ajustes —solo su perfil—, así que
   * su portal pasa `/perito/perfil`. Por defecto, los Ajustes de siempre.
   */
  settingsTo?: string;
  /** Destino de «Ver todas» las notificaciones. */
  notificationsTo?: string;
}

const RECENT_LIMIT = 3;

export function ProfileSheet({ open, onOpenChange, settingsTo, notificationsTo = "/notifications" }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, company, role, signOut, profile } = useAuth();
  const fullName = user?.user_metadata?.full_name || user?.email || "";
  const { active: onboardingActive } = useOnboardingGate();

  const { notifications, unreadCount, markAsRead } = useNotifications();
  const avatar = useFotoOffline(profile?.avatar_url);
  const recent = notifications.slice(0, RECENT_LIMIT);

  const roleLabel = role ? t(`roles.${role}`, "") : "";
  const subtitle = [roleLabel, company?.name].filter(Boolean).join(" · ");

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      {/* Corta a propósito: es el menú de la cuenta, no una pantalla. Pero en el
          teléfono abre alto —cubriendo la mayor parte del viewport (72dvh)—: es el
          menú de la cuenta y pide presencia. Con poco contenido no deja un hueco
          muerto abajo —el cuerpo se estira (`max-md:flex-1`) y el sobrante se lo
          queda la caja de notificaciones, que queda un poco más vacía—, y el tope
          de siempre (72dvh) coincide con ese suelo, así que abre fija y llena. */}
      <DrawerContent fit="short" className={cn("border-none", SHEET_ACCOUNT_MIN_H_CLASS)}>
        {/* Esquina: el acceso a la cuenta y la X, UNO AL LADO DEL OTRO. Apilados
            ocupaban dos alturas y empujaban la rueda hasta media foto de perfil;
            en fila ocupan una sola banda y el hueco que hay que reservar en la
            fila del nombre es ancho pero de una sola línea. Ahora la banda baja
            a la altura del nombre (mismo renglón que «{fullName}»), en el hueco
            que ya reserva `pr-[104px]`. La X se queda en la esquina, que es
            donde el dedo la busca. */}
        <div className="absolute right-[18px] top-[41px] z-10 flex flex-row-reverse items-center gap-2.5">
          <DrawerClose className={SHEET_CLOSE_CLASS} aria-label={t("common.close")}>
            <SheetCloseIcon />
          </DrawerClose>
          <button
            onClick={() => go(settingsTo ?? (onboardingActive ? "/settings/my-account" : "/settings"))}
            aria-label={onboardingActive ? t("nav.profile", "Profile") : t("nav.settings")}
            title={onboardingActive ? t("nav.profile", "Profile") : t("nav.settings")}
            /* Solo icono en todos los tamaños: la píldora con texto de escritorio
               tenía un ancho variable, y apilada bajo la X se comía el nombre en
               cuanto el texto crecía con el idioma. */
            className="inline-flex items-center justify-center active:scale-95 transition-all
              w-[34px] h-[34px] rounded-full bg-white text-black border border-[#b6dded] shadow-sm hover:bg-slate-50"
          >
            {onboardingActive ? <User className="w-5 h-5" /> : <Settings className="w-5 h-5" />}
          </button>
        </div>
        <DrawerTitle className="sr-only">{fullName}</DrawerTitle>
        <DrawerDescription className="sr-only">
          {t("nav.profile", "Profile")}
        </DrawerDescription>

        <div className="flex flex-col min-h-0 max-md:flex-1 overflow-y-auto px-5 pt-2 pb-[calc(max(var(--safe-bottom),16px)+8px)]">
          {/* Lo que reserva la esquina: 18 de margen + 34 + 34 de los dos botones
              + 10 de hueco entre ellos = 96, más aire. Es lo que impide que el
              nombre pase por debajo de la rueda o de la X. */}
          <div className="flex items-center gap-3.5 py-4 pr-[104px]">
            <div className="w-14 h-14 rounded-full ipm-gradient-main flex items-center justify-center text-white font-bold text-xl shrink-0 overflow-hidden">
              {avatar ? (
                <img src={avatar} alt={fullName} className="w-full h-full object-cover" />
              ) : (
                (fullName || "U").charAt(0).toUpperCase()
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xl font-bold tracking-tight text-foreground truncate">
                {fullName}
              </p>
              {subtitle && (
                <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
              )}
            </div>
          </div>

          {/* Recent notifications. En el teléfono se lleva el sobrante del suelo
              de 50dvh (`max-md:flex-1`): así el hueco cuando hay pocas queda DENTRO
              de la caja y no como banda muerta bajo «Cerrar sesión», que se ancla
              abajo. */}
          <div className="mt-4 max-md:flex-1 rounded-2xl border border-[#b6dded] bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <h3 className="text-sm font-bold text-foreground">
                {t("notifications.recent", "Notificaciones recientes")}
              </h3>
              <button
                onClick={() => go(notificationsTo)}
                className="inline-flex items-center gap-0.5 text-sm font-semibold text-[#2B7DE0] hover:opacity-80"
              >
                {t("notifications.viewAll", "Ver todas")}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {recent.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <p className="text-sm">{t("notifications.empty")}</p>
              </div>
            ) : (
              <div className="px-2 pb-2 space-y-0.5">
                {recent.map((n) => (
                  <NotificationCard
                    key={n.id}
                    notification={n}
                    onMarkAsRead={markAsRead}
                    onNavigate={() => onOpenChange(false)}
                    variant="compact"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Lo que se guarda en el teléfono para cuando no hay cobertura. */}
          <DatosEnEsteMovil className="mt-4" />

          {/* Sign out */}
          <button
            onClick={() => {
              onOpenChange(false);
              signOut();
            }}
            className={cn(
              "mt-4 w-full flex items-center justify-center gap-2 py-4 rounded-2xl",
              "border-0 bg-destructive text-white font-semibold shadow-sm shadow-destructive/30",
              "hover:bg-destructive/90 active:scale-[0.99] transition-all",
            )}
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm">{t("auth.signOut")}</span>
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
